import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { styled } from '@abyss/web/tools/styled';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Layout } from '@abyss/web/ui/Layout';
import { Pagination, ResultCount } from '@abyss/web/ui/Pagination';
import { Text } from '@abyss/web/ui/Text';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import { ChoosePCPAlert } from '../../../../common/Alert/ChoosePCPAlert';
import { CompareShareToggle } from '../../../../common/CompareShareToggle';
import {
  Constants,
  CoverageTypesCodes,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { FilterButtons } from '../../../../common/ContentFilters/FilterButtons';
import { DataCardContainer } from '../../../../common/DataCard/DataCardContainer';
import { EAPCode } from '../../../../common/EAPCode';
import { getFeatureFlag } from '../../../../common/Utils';
import {
  isValidCareCategory,
  usePopoverContentBySectionType,
} from '../../../../common/Utils/getProviderType';
import { isUmrMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { SmartChoiceInfoChip } from '../../../../common/Widget/SmartChoiceWidget/SmartChoiceInfoChip';
import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useLagoon } from '../../../../hooks/useLagoon';
import { CompareProvider } from '../../../../models/Provider';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { loginNotRequired } from '../../../../utils/session.utils';
import { AllPaginationProps } from '../MapView/AllPaginationProps';
import { ConfirmationModal } from '../MapView/CompareDrawer/Components/ConfirmationModal';
import { DataCardList, DataCardListStyled } from '../MapView/DataCardList';
import { MapViewTopPagination } from '../MapView/MapViewTopPagination';
import { FilterProviderResultsPrintViewProps } from '../MapView/Print/FilterProviderResultsPrintView';
import { VirtualCareProvidersBanner } from '../MapView/VirtualCareProvidersBanner/VirtualCareProvidersBanner';
import { DownloadResultsButton } from './DownloadResultsButton';
import { ShareCompareButton } from './ShareCompareButton';
import {
  EnhancePaginationMobileStyles,
  MobileFilterContainer,
  MobileResultContainer,
} from './ViewAll.style';

const EAPStyle = styled('div', {
  padding: '4px 0 12px 0',
});

const TitleContainer = styled('div', {
  alignItems: 'center',
});

const ListViewWrapper = styled('div', {
  dynamic: ({ cssProps: { openMapview, navigateToDirections } }) => ({
    display: openMapview || navigateToDirections ? 'none' : 'block',
  }),
});

type Props = {
  heading: string;
  headers: ResponseHeaders;
  openCompare?: boolean;
  isLoading?: boolean;
  openShare?: boolean;
  pageSize?: number;
  paginationProps?: any;
  gotoPage?: any;
  nextPage?: any;
  previousPage?: any;
  results?: any[];
  search?: string | null;
  searchTerm?: string;
  sectionType?: string;
  selectedCheckbox?: any;
  totalResultsCount?: number;
  addSelectedProvider: (a: any) => void;
  selectedFilters?: string;
  setOpenCompare: (a: boolean) => void;
  setOpenMapview: (a: boolean) => void;
  setOpenShare: (a: boolean) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  allPaginationProps: AllPaginationProps;
  navigateToDirections?: boolean;
  setNavigateToDirections?: (a: boolean) => void;
  map: any;
  openMapview: boolean;
  mobileRouteView?: boolean;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  isMNR?: boolean;
  setOpenBoxContents: (a: boolean) => void;
  enableMapViewEnhancements: boolean;
  enableMapFilterSortCTA: boolean;
  choosePCP: boolean;
  compareShareToggle: boolean;
  setCompareShareToggle: (a: boolean) => void;
  filterProviderResultsPrintViewProps?: FilterProviderResultsPrintViewProps;
  expandedProviderListView?: boolean;
  setIsCompareClicked: () => void;
  enableMapViewHideOption?: boolean;
  chipValue?: string | undefined;
  setVirtualCareListView: (a: boolean) => void;
  recommendationPreferences?: any;
};

export const MobileListView = ({
  compareShareToggle,
  heading,
  headers,
  openCompare = false,
  openShare = false,
  isLoading = false,
  pageSize = 10,
  paginationProps,
  results = [],
  search,
  sectionType,
  selectedCheckbox = { checked: {} },
  totalResultsCount = 0,
  gotoPage,
  nextPage,
  previousPage,
  addSelectedProvider,
  selectedFilters,
  setCompareShareToggle,
  setOpenCompare,
  setOpenShare,
  setOpenMapview,
  setSelectedCheckbox,
  setSelectedItems,
  allPaginationProps,
  searchTerm,
  navigateToDirections = false,
  setNavigateToDirections,
  map,
  openMapview = false,
  mobileRouteView = false,
  selectLocation,
  isMNR,
  setOpenBoxContents,
  enableMapViewEnhancements,
  enableMapFilterSortCTA,
  choosePCP,
  filterProviderResultsPrintViewProps,
  expandedProviderListView,
  setIsCompareClicked,
  enableMapViewHideOption = false,
  chipValue,
  setVirtualCareListView,
  recommendationPreferences,
}: Props) => {
  const { t } = useTranslation();
  const modal = useOverlay('mobile-list-view-confirmation-modal');
  const translatedHeadingText = heading?.length
    ? t(`${heading}`)
    : t(`${search}`) || '';
  const pageHeader = `${t(
    `${sectionType}-results-for`
  )} ${translatedHeadingText}`;
  const mapViewSectionType = sectionType?.toLowerCase();
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const { pageSizeOptions, setPageSize, setLastPageSize, pageNumber } =
    allPaginationProps;
  const coverageType = useCoverageType();
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();

  const eapCodeFlag = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.EAP_CODE
  );
  const showMergedCompareAndShare = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS?.ENABLE_COMPARE_SMART_CHOICE
  );
  const enableFontHeaderMapView = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FONT_HEADER_MAP_VIEW
  );
  const enableVirtualCareEpic = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC
  );
  const enableVirtualCareProvidersInMapResultPage = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_PROVIDERS_MAP_RESULT_PAGE
  );

  const allConfigs = useLagoon('config')();

  const compareButtonRelocation = allConfigs?.find(
    (row) => row.key === 'ENABLE_COMPARE_BUTTON_RELOCATION'
  )?.value;

  const enableCompareButtonRelocation =
    compareButtonRelocation === 'Y' ? true : false;

  const virtualCareProviders = useVirtualCareStore(
    (state) => state.virtualCareProviders
  );

  const isOpenCompareShare = openCompare || openShare;
  const isStandalone = loginNotRequired();
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const [toggle, setToggle] = useState<boolean>(compareShareToggle);
  const urlCheck: boolean = !(isStandalone || isUmr);

  const isBHCare: boolean =
    coverageType ===
    ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH];
  const topPaginationRef = useRef<HTMLDivElement>(null);
  const mapViewViewAllLinkTrack = (name: String, loc: String) => {
    adobeLinkTrackEvent({
      name,
      location: loc,
      type: 'internal',
      destinationUrl: '',
    });
  };
  const previousPageTop = () => {
    mapViewViewAllLinkTrack(
      'previous page',
      `body:top pagination control:page ${pageNumber}`
    );
    previousPage();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const nextPageTop = () => {
    mapViewViewAllLinkTrack(
      'next page',
      `body:top pagination control:page ${pageNumber}`
    );
    nextPage();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const setPageSizeFunc = (pageSizeValue) => {
    mapViewViewAllLinkTrack(
      `results per page:${pageSizeValue}`,
      `body:top pagination control:page ${pageNumber}`
    );
    setLastPageSize(pageSize);
    setPageSize(pageSizeValue);
    gotoPage(0);
  };

  const hasPagination: boolean = totalResultsCount > pageSize;
  const isValidCategory = isValidCareCategory(chipValue, sectionType);
  const popoverContent = usePopoverContentBySectionType(sectionType || '');
  useEffect(() => {
    if (openMapview && map?.current) map.current.resize();
  }, [openMapview, map]);

  const stickyHeaderDisabled = portalData?.portalConfig?.stickyHeaderDisabled;

  const headingWhenPaginationDisabled = `${totalResultsCount} ${t(
    `${mapViewSectionType} results for`
  )}`;

  const searchTermText = (
    <span style={{ fontWeight: '700', fontSize: '16px!important' }}>
      {' '}
      {translatedHeadingText}{' '}
    </span>
  );

  const headingTextForMapView = (
    <span>
      {headingWhenPaginationDisabled}
      {searchTermText}
    </span>
  );

  const handleCompareShareToggle = (isChecked: boolean) => {
    if (isChecked) {
      adobeLinkTrackEvent({
        name: 'Compare and share a pdf',
        location: 'Toggle',
        type: 'Internal',
      });
    } else {
      adobeModalTrackEvent('are you sure you want to exit');
      setToggle(true);
      modal?.open();
    }
  };
  useEffect(() => {
    if (!compareShareToggle) {
      setToggle(false);
    }
  }, [compareShareToggle]);
  const handleClickCb = () => {
    setToggle(false);
    setCompareShareToggle(false);
    setOpenCompare(false);
  };

  return (
    <ListViewWrapper
      cssProps={{
        openMapview,
        navigateToDirections,
      }}
    >
      {!compareShareToggle && (
        <React.Fragment>
          {!enableMapViewEnhancements && (
            <>
              <ChoosePCPAlert isMapView />
              <TitleContainer>
                <Heading
                  css={{
                    '&.abyss-heading-root': {
                      fontSize: '$mediumText',
                      lineHeight: '$lg',
                      fontFamily: '$primary',
                    },
                  }}
                  data-auto-testid="results-heading"
                  data-testid="results-heading"
                  offset={0}
                  textAlign="left"
                >
                  {pageHeader}
                </Heading>
              </TitleContainer>
            </>
          )}
          <MobileFilterContainer
            cssProps={{
              urlCheck,
              enableMapFilterSortCTA,
              stickyHeaderDisabled,
            }}
          >
            <FilterButtons
              enableMapViewEnhancements={enableMapViewEnhancements}
              openCompare={openCompare}
              openMapview={openMapview}
              openShare={openShare}
              sectionType={sectionType || ''}
              setCompareShareToggle={setCompareShareToggle}
              setOpenBoxContents={setOpenBoxContents}
              setOpenCompare={setOpenCompare}
              setOpenMapview={setOpenMapview}
              setOpenShare={setOpenShare}
              setSelectedCheckbox={setSelectedCheckbox}
              setSelectedItems={setSelectedItems}
            />
          </MobileFilterContainer>
        </React.Fragment>
      )}

      <Layout.Stack
        alignItems="left"
        css={
          enableMapViewEnhancements &&
          !isOpenCompareShare &&
          EnhancePaginationMobileStyles
        }
        grow
      >
        {!enableMapViewEnhancements && eapCodeFlag && isBHCare ? (
          <EAPStyle css={{ paddingLeft: '24px' }}>
            <EAPCode />
          </EAPStyle>
        ) : null}
        {isLoading ? (
          <DataCardListStyled
            data-auto-testid="skeleton-wrapper"
            data-testid="skeleton-wrapper"
          >
            {Array.from(Array(10).keys()).map((item) => (
              <DataCardContainer
                fromMobileListView
                isLoading={isLoading}
                key={item}
              />
            ))}
          </DataCardListStyled>
        ) : (
          <React.Fragment>
            {enableMapViewEnhancements ? (
              <MobileResultContainer
                cssProps={{
                  choosePCP,
                  urlCheck,
                  enableMapFilterSortCTA,
                  stickyHeaderDisabled,
                  compareShareToggle,
                }}
              >
                {isValidCategory && (
                  <SmartChoiceInfoChip
                    popoverContent={popoverContent}
                    wrapperStyle={{
                      marginTop: '-4px',
                    }}
                  />
                )}
                <Text
                  css={{
                    'abyss-text-root': {
                      marginBottom: '8px',
                      width: '375px',
                      '@media (max-width: 400px)': {
                        width: '100%',
                      },
                    },
                  }}
                  data-auto-testid="total-count-results-heading"
                  data-testid="total-count-results-heading"
                >
                  {enableFontHeaderMapView ? (
                    headingTextForMapView
                  ) : hasPagination ? (
                    <ResultCount
                      {...paginationProps}
                      css={{
                        'abyss-pagination-results-text': {
                          border: '0px',
                          margin: '0 2px ',
                          fontSize: '14px !important',
                          lineHeight: '18px !important',
                          fontWeight: '400 !important',
                          color: '#4B4D4F',
                        },
                      }}
                      customNoResultsLabel={t('PAGINATION.NO_RESULTS_LABEL')}
                      customOfLabel={t('PAGINATION.OF_LABEL')}
                      customResultLabel={t(`${mapViewSectionType} result for`)}
                      customResultsLabel={t(
                        `${mapViewSectionType} results for`
                      )}
                      customShowingLabel={' '}
                      pageSize={pageSize}
                      resultsTotalCount={totalResultsCount}
                    />
                  ) : (
                    `${totalResultsCount} ${t('of')} ${totalResultsCount} ${t(
                      `${mapViewSectionType} results for`
                    )}`
                  )}
                </Text>
                <TitleContainer
                  css={{ alignItems: enableMapViewEnhancements && 'start' }}
                >
                  {!enableFontHeaderMapView && (
                    <Heading
                      css={{
                        '&.abyss-heading-root': {
                          fontSize: '$mediumText',
                          lineHeight: '$lg',
                          paddingBottom: '4px',
                          width: '375px',
                        },
                      }}
                      data-auto-testid="results-heading"
                      data-testid="results-heading"
                      offset={3}
                      textAlign="left"
                    >
                      {translatedHeadingText}
                    </Heading>
                  )}
                </TitleContainer>
                <Flex
                  css={{
                    paddingBottom: '10px',
                    alignItems: 'center',
                    '.abyss-text-root': {
                      fontSize: '14px',
                      lineHeight: '18px',
                    },
                    '.abyss-icon-material': {
                      height: '18px',
                      width: '18px',
                    },
                  }}
                >
                  {showMergedCompareAndShare &&
                  sectionType === Constants.RESULT_SECTION.PROVIDER ? (
                    <CompareShareToggle
                      data-testid="compare-share-toggle-mobile-list-view"
                      dataTestId="compare-share-toggle-mobile-list-view"
                      isChecked={toggle}
                      onChange={(e) => {
                        handleCompareShareToggle(e.target.checked);
                        if (e.target.checked) {
                          setToggle(true);
                          setCompareShareToggle(true);
                          setOpenCompare(true);
                        }
                      }}
                      title={t('COMPARE_BUTTONS.COMPARE_AND_SHARE')}
                    ></CompareShareToggle>
                  ) : (
                    <>
                      {sectionType === Constants.RESULT_SECTION.PROVIDER && (
                        <ShareCompareButton
                          enableMapViewEnhancements={enableMapViewEnhancements}
                          isShareButton={false}
                          openCompare={openCompare}
                          sectionType={sectionType}
                          setIsClicked={setIsClicked}
                          setOpenCompare={setOpenCompare}
                          setOpenShare={setOpenShare}
                          setSelectedCheckbox={setSelectedCheckbox}
                          setSelectedItems={setSelectedItems}
                          text={t('COMPARE_BUTTONS.COMPARE')}
                          view="enhancement"
                        />
                      )}
                      <ShareCompareButton
                        enableMapViewEnhancements={enableMapViewEnhancements}
                        openShare={openShare}
                        sectionType={sectionType}
                        setIsClicked={setIsClicked}
                        setOpenCompare={setOpenCompare}
                        setOpenShare={setOpenShare}
                        setSelectedCheckbox={setSelectedCheckbox}
                        setSelectedItems={setSelectedItems}
                        text={t('COMPARE_BUTTONS.SHARE')}
                        view="enhancement"
                      />
                    </>
                  )}
                  {!enableMapViewHideOption && (
                    <DownloadResultsButton
                      enableMapViewEnhancements={enableMapViewEnhancements}
                      filterProviderResultsPrintViewProps={
                        filterProviderResultsPrintViewProps
                      }
                      isClicked={isClicked}
                      setCompareShareToggle={setCompareShareToggle}
                      setIsClicked={setIsClicked}
                      setOpenBoxContents={setOpenBoxContents}
                      setOpenCompare={setOpenCompare}
                      setOpenShare={setOpenShare}
                      setSelectedCheckbox={setSelectedCheckbox}
                      setSelectedItems={setSelectedItems}
                      text={t('COMPARE_BUTTONS.DOWNLOAD_RESULTS')}
                      view="enhancement"
                    />
                  )}
                </Flex>
              </MobileResultContainer>
            ) : (
              <ResultCount
                {...paginationProps}
                css={{
                  'abyss-pagination-results-root': {
                    fontWeight: '$semibold',
                  },
                }}
                customNoResultsLabel={t('PAGINATION.NO_RESULTS_LABEL')}
                customOfLabel={t('PAGINATION.OF_LABEL')}
                customResultLabel={t('PAGINATION.ONE_RESULT_LABEL')}
                customResultsLabel={t('PAGINATION.RESULTS_LABEL')}
                customShowingLabel={t('PAGINATION.SHOWING_LABEL')}
                pageSize={pageSize}
                resultsTotalCount={totalResultsCount}
              />
            )}
            <Flex
              css={{
                justifyContent: enableMapViewEnhancements ? 'center' : '',
                paddingLeft: enableCompareButtonRelocation ? '16px' : '',
              }}
            >
              {enableVirtualCareEpic &&
                enableVirtualCareProvidersInMapResultPage &&
                pageNumber === 1 &&
                virtualCareProviders &&
                Object.keys(virtualCareProviders).length > 0 && (
                  <VirtualCareProvidersBanner
                    expandedProviderListView={!!expandedProviderListView}
                    setVirtualCareListView={setVirtualCareListView}
                    virtualCareProviders={virtualCareProviders}
                  />
                )}
              <DataCardList
                addSelectedProvider={addSelectedProvider}
                enableMapViewEnhancements={enableMapViewEnhancements}
                expandedProviderListView={expandedProviderListView}
                fromMobileListView
                headers={headers}
                isLoading={isLoading}
                isMNR={isMNR}
                locationForAnalytics={`${sectionType} results for ${search}`}
                map={map}
                mobileRouteView={mobileRouteView}
                navigateToDirections={navigateToDirections}
                pageNumber={pageNumber}
                providerResults={results}
                recommendationPreferences={recommendationPreferences}
                searchTerm={searchTerm}
                sectionType={sectionType || ''}
                selectLocation={selectLocation}
                selectedCheckbox={selectedCheckbox}
                selectedFilters={selectedFilters}
                setIsCompareClicked={setIsCompareClicked}
                setNavigateToDirections={setNavigateToDirections}
                setSelectedCheckbox={setSelectedCheckbox}
                showCheckboxes={openShare || openCompare}
                updatePin={() => null}
                usedAt={Constants.MOBILE_LIST_VIEW}
              />
            </Flex>
          </React.Fragment>
        )}
        <Flex alignItems="center" direction="column">
          {enableMapViewEnhancements ? (
            <React.Fragment>
              {hasPagination && (
                <MapViewTopPagination
                  enableMapViewEnhancements={enableMapViewEnhancements}
                  isLoading={isLoading}
                  nextPageTop={nextPageTop}
                  pageSize={pageSize}
                  pageSizeOptions={pageSizeOptions}
                  paginationProps={paginationProps}
                  previousPageTop={previousPageTop}
                  setPageSizeFunc={setPageSizeFunc}
                  topPaginationRef={topPaginationRef}
                />
              )}
            </React.Fragment>
          ) : (
            <Pagination
              {...paginationProps}
              css={{
                'abyss-pagination-page-button': {
                  minWidth: '20px',
                },
                'abyss-pagination-arrow': {
                  paddingLeft: '4px',
                  paddingRight: '4px',
                },
              }}
              customNoResultsLabel={t('PAGINATION.NO_RESULTS_LABEL')}
              customOfLabel={t('PAGINATION.OF_LABEL')}
              customResultLabel={t('PAGINATION.ONE_RESULT_LABEL')}
              customResultsLabel={t('PAGINATION.RESULTS_LABEL')}
              customShowingLabel={t('PAGINATION.SHOWING_LABEL')}
              data-auto-testid="pagination-component"
              data-testid="pagination-component"
              gotoPage={gotoPage}
              nextPage={nextPage}
              pageSize={pageSize}
              previousPage={previousPage}
            />
          )}
        </Flex>
      </Layout.Stack>
      <ConfirmationModal
        handleClickCb={handleClickCb}
        modal={modal}
        modalName={'mobile-list-view-confirmation-modal'}
        setOpenCompare={setOpenCompare}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
      />
      {enableMapViewHideOption && (
        <Flex justify="center">
          <DownloadResultsButton
            enableMapViewEnhancements={enableMapViewEnhancements}
            filterProviderResultsPrintViewProps={
              filterProviderResultsPrintViewProps
            }
            isClicked={isClicked}
            setCompareShareToggle={setCompareShareToggle}
            setIsClicked={setIsClicked}
            setOpenBoxContents={setOpenBoxContents}
            setOpenCompare={setOpenCompare}
            setOpenShare={setOpenShare}
            setSelectedCheckbox={setSelectedCheckbox}
            setSelectedItems={setSelectedItems}
            text={t('COMPARE_BUTTONS.DOWNLOAD_PRINT_RESULTS')}
            view="enhancement"
          />
        </Flex>
      )}
    </ListViewWrapper>
  );
};
